import { useState, useEffect } from "react";
import API from "../../services/api";

interface User {
  _id: string;
  full_name: string;
  email: string;
  phone_prefix: string;
  phone_number: string;
  address: string;
  query?: string;
}

interface EditUserModalProps {
  user: User;
  onClose: () => void;
  onSuccess: () => void;
}

interface ValidationError {
  type: string;
  msg: string;
  path: string;
  location: string;
}

export default function EditUserModal({ user, onClose, onSuccess }: EditUserModalProps) {
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone_prefix: "",
    phone_number: "",
    address: "",
    query: ""
  });
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [generalError, setGeneralError] = useState<string | null>(null);

  useEffect(() => {
    setFormData({
      full_name: user.full_name,
      email: user.email || "",
      phone_prefix: user.phone_prefix,
      phone_number: user.phone_number,
      address: user.address,
      query: user.query || ""
    });
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setFieldErrors({});
    setGeneralError(null);

    try {
      const res = await API.put(`/user/${user._id}`, formData);
      if (res.data && res.data.success) {
        onSuccess();
      }
    } catch (error: any) {
      if (error?.response?.data?.data && Array.isArray(error.response.data.data)) {
        const errors: { [key: string]: string } = {};
        error.response.data.data.forEach((err: ValidationError) => {
          errors[err.path] = err.msg;
        });
        setFieldErrors(errors);
      } else {
        setGeneralError(error?.response?.data?.message || "Failed to update user");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (fieldErrors[e.target.name]) {
      setFieldErrors({
        ...fieldErrors,
        [e.target.name]: ""
      });
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Edit User</h2>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Full Name *</label>
            <input
              type="text"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              placeholder="Enter full name"
              style={{
                ...styles.input,
                ...(fieldErrors.full_name ? styles.inputError : {})
              }}
            />
            {fieldErrors.full_name && (
              <span style={styles.fieldError}>{fieldErrors.full_name}</span>
            )}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter email address"
              style={{
                ...styles.input,
                ...(fieldErrors.email ? styles.inputError : {})
              }}
            />
            {fieldErrors.email && (
              <span style={styles.fieldError}>{fieldErrors.email}</span>
            )}
          </div>

          <div style={styles.formRow}>
            <div style={{ ...styles.formGroup, flex: 1 }}>
              <label style={styles.label}>Phone Prefix *</label>
              <input
                type="text"
                name="phone_prefix"
                value={formData.phone_prefix}
                onChange={handleChange}
                placeholder="+1"
                style={{
                  ...styles.input,
                  ...(fieldErrors.phone_prefix ? styles.inputError : {})
                }}
              />
              {fieldErrors.phone_prefix && (
                <span style={styles.fieldError}>{fieldErrors.phone_prefix}</span>
              )}
            </div>
            <div style={{ ...styles.formGroup, flex: 2 }}>
              <label style={styles.label}>Phone Number *</label>
              <input
                type="text"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
                placeholder="Enter phone number"
                style={{
                  ...styles.input,
                  ...(fieldErrors.phone_number ? styles.inputError : {})
                }}
              />
              {fieldErrors.phone_number && (
                <span style={styles.fieldError}>{fieldErrors.phone_number}</span>
              )}
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Address *</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Enter address"
              style={{
                ...styles.input,
                ...(fieldErrors.address ? styles.inputError : {})
              }}
            />
            {fieldErrors.address && (
              <span style={styles.fieldError}>{fieldErrors.address}</span>
            )}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Feedback</label>
            <textarea
              name="query"
              value={formData.query}
              onChange={handleChange}
              placeholder="Enter feedback"
              rows={4}
              style={{
                ...styles.input,
                resize: "vertical",
                ...(fieldErrors.query ? styles.inputError : {})
              }}
            />
            {fieldErrors.query && (
              <span style={styles.fieldError}>{fieldErrors.query}</span>
            )}
          </div>

          {generalError && (
            <div style={styles.errorMessage}>
              {generalError}
            </div>
          )}

          <div style={styles.modalFooter}>
            <button type="button" onClick={onClose} style={styles.cancelButton}>
              Cancel
            </button>
            <button type="submit" disabled={loading} style={styles.submitButton}>
              {loading ? "Updating..." : "Update User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "32px",
    maxWidth: "600px",
    width: "90%",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
  },
  modalTitle: {
    fontSize: "22px",
    fontWeight: "600",
    color: "#1a2332",
    margin: 0,
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "24px",
    cursor: "pointer",
    color: "#666",
    padding: "0 4px",
  },
  formGroup: {
    marginBottom: "16px",
  },
  formRow: {
    display: "flex",
    gap: "12px",
  },
  label: {
    display: "block",
    fontSize: "14px",
    fontWeight: "500",
    color: "#555",
    marginBottom: "6px",
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "14px",
    outline: "none",
    transition: "border-color 0.2s",
    boxSizing: "border-box",
  },
  inputError: {
    borderColor: "#f44336",
    backgroundColor: "#fff3f3",
  },
  fieldError: {
    display: "block",
    marginTop: "4px",
    fontSize: "12px",
    color: "#f44336",
  },
  errorMessage: {
    padding: "10px 14px",
    backgroundColor: "#fff3f3",
    border: "1px solid #f44336",
    borderRadius: "6px",
    color: "#d32f2f",
    fontSize: "14px",
    marginBottom: "16px",
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    marginTop: "24px",
    paddingTop: "16px",
    borderTop: "1px solid #e8ecf1",
  },
  cancelButton: {
    padding: "10px 20px",
    backgroundColor: "#f5f5f5",
    color: "#666",
    border: "1px solid #ddd",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  submitButton: {
    padding: "10px 24px",
    backgroundColor: "#6c5ce7",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    transition: "background-color 0.2s",
  },
};